import './style.css';
import { parseMarkdownVocabulary } from './parser.js';
import { applyReview, createReviewRecord, getDueCards, initialSm2State } from './scheduler.js';
import { loadState, saveState } from './storage.js';
import type { Card, ReviewRecord } from './types.js';

interface AppState {
  cards: Card[];
  reviews: ReviewRecord[];
  dueCards: Card[];
  currentIndex: number;
  flipped: boolean;
  message: string | null;
  messageType: 'info' | 'success' | 'error';
}

const state: AppState = {
  cards: [],
  reviews: [],
  dueCards: [],
  currentIndex: 0,
  flipped: false,
  message: null,
  messageType: 'info',
};

function setMessage(text: string | null, type: AppState['messageType'] = 'info'): void {
  state.message = text;
  state.messageType = type;
}

function loadPersistedState(): void {
  const persisted = loadState();
  state.cards = persisted.cards;
  state.reviews = persisted.reviews;
  recomputeDue();
}

function recomputeDue(): void {
  state.dueCards = getDueCards(state.cards);
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function importCardsFromText(text: string): void {
  const result = parseMarkdownVocabulary(text);
  if (result.errors.length > 0) {
    setMessage(`导入失败：\n${result.errors.join('\n')}`, 'error');
    render();
    return;
  }

  if (result.cards.length === 0) {
    setMessage('未找到有效词汇，请检查文件格式。', 'error');
    render();
    return;
  }

  const now = Date.now();
  const newCards: Card[] = result.cards.map((line) => ({
    id: generateId(),
    hanzi: line.hanzi,
    pinyin: line.pinyin,
    phrase: line.phrase,
    createdAt: now,
    sm2: initialSm2State(),
  }));

  state.cards = [...state.cards, ...newCards];
  state.reviews = [...state.reviews];
  saveState(state.cards, state.reviews);
  recomputeDue();
  state.currentIndex = 0;
  state.flipped = false;
  setMessage(`成功导入 ${newCards.length} 张卡片。`, 'success');
  render();
}

function handleFile(file: File): void {
  const reader = new FileReader();
  reader.onload = () => {
    const text = typeof reader.result === 'string' ? reader.result : '';
    importCardsFromText(text);
  };
  reader.onerror = () => {
    setMessage('读取文件失败，请重试。', 'error');
    render();
  };
  reader.readAsText(file);
}

function rateCurrentCard(rating: number): void {
  const card = state.dueCards[state.currentIndex];
  if (!card) return;

  card.sm2 = applyReview(card.sm2, rating);
  state.reviews.push(createReviewRecord(card.id, rating));
  saveState(state.cards, state.reviews);

  state.flipped = false;
  recomputeDue();

  if (state.currentIndex >= state.dueCards.length) {
    state.currentIndex = 0;
  }

  if (state.dueCards.length === 0) {
    setMessage('太棒了！今日复习已完成 🎉', 'success');
  }

  render();
}

function showNextCard(): void {
  if (state.dueCards.length === 0) return;
  state.currentIndex = (state.currentIndex + 1) % state.dueCards.length;
  state.flipped = false;
  render();
}

function showPreviousCard(): void {
  if (state.dueCards.length === 0) return;
  state.currentIndex = (state.currentIndex - 1 + state.dueCards.length) % state.dueCards.length;
  state.flipped = false;
  render();
}

function flipCard(): void {
  if (state.dueCards.length === 0) return;
  state.flipped = !state.flipped;
  render();
}

function speak(text: string): void {
  if (!('speechSynthesis' in window)) {
    setMessage('当前浏览器不支持语音朗读。', 'error');
    render();
    return;
  }
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'zh-CN';
  window.speechSynthesis.speak(utterance);
}

function render(): void {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) return;

  app.innerHTML = `
    <header class="app-header">
      <h1>儿童拼音闪卡</h1>
      <div class="queue-badge" aria-label="今日复习队列">
        今日队列 <span class="badge-count">${state.dueCards.length}</span>
      </div>
    </header>

    <main class="app-main">
      ${renderMessage()}
      ${renderImportSection()}
      ${renderFlashcardSection()}
    </main>
  `;

  bindEvents();
}

function renderMessage(): string {
  if (!state.message) return '';
  return `<div class="message message-${state.messageType}" role="alert">${escapeHtml(state.message).replace(/\n/g, '<br>')}</div>`;
}

function renderImportSection(): string {
  return `
    <section class="import-section">
      <h2>导入词汇</h2>
      <p>拖拽 Markdown 文件到下方，或直接在粘贴区输入。</p>
      <div class="drop-zone" id="drop-zone" tabindex="0" role="button" aria-label="拖放文件到此处">
        <span class="drop-zone-icon">📂</span>
        <span>拖放 .md 文件到此处</span>
      </div>
      <textarea
        id="paste-area"
        class="paste-area"
        rows="5"
        placeholder="- 汉字 拼音 组词例子&#10;- 春 chūn 春天&#10;- 花 huā 花朵"
        aria-label="粘贴词汇"
      ></textarea>
      <button id="import-paste" type="button" class="btn btn-primary">导入粘贴内容</button>
      <p class="hint">每行格式：- 汉字 拼音 组词</p>
    </section>
  `;
}

function renderFlashcardSection(): string {
  if (state.dueCards.length === 0) {
    return `
      <section class="flashcard-section empty-state">
        <div class="empty-illustration">📚</div>
        <h2>暂无今日复习卡片</h2>
        <p>导入词库后开始复习，或明天再来。</p>
      </section>
    `;
  }

  const card = state.dueCards[state.currentIndex];
  const progressText = `${state.currentIndex + 1} / ${state.dueCards.length}`;

  return `
    <section class="flashcard-section">
      <div class="progress-bar" aria-label="复习进度">
        <div class="progress-fill" style="width: ${((state.currentIndex + 1) / state.dueCards.length) * 100}%"></div>
      </div>
      <p class="progress-text">${progressText}</p>

      <div class="flashcard ${state.flipped ? 'flipped' : ''}" id="flashcard" role="button" tabindex="0" aria-label="闪卡，点击或按空格翻面">
        <div class="flashcard-inner">
          <div class="flashcard-face flashcard-front">
            <span class="hanzi">${escapeHtml(card.hanzi)}</span>
            <span class="flip-hint">点击或按空格翻到背面</span>
          </div>
          <div class="flashcard-face flashcard-back">
            <span class="hanzi-back">${escapeHtml(card.hanzi)}</span>
            <span class="pinyin">${escapeHtml(card.pinyin)}</span>
            <span class="phrase">${escapeHtml(card.phrase)}</span>
            <button id="speak-btn" type="button" class="speak-btn" aria-label="朗读汉字">
              🔊 听发音
            </button>
          </div>
        </div>
      </div>

      <div class="rating" role="group" aria-label="评分">
        <button class="rating-btn rating-1" data-rating="1" type="button">1 生疏</button>
        <button class="rating-btn rating-2" data-rating="2" type="button">2 困难</button>
        <button class="rating-btn rating-3" type="button" data-rating="3">3 掌握</button>
        <button class="rating-btn rating-4" data-rating="4" type="button">4 熟练</button>
      </div>
      <p class="rating-hint">空格翻面 · 1–4 评分 · ← → 切换</p>
    </section>
  `;
}

function bindEvents(): void {
  const dropZone = document.getElementById('drop-zone');
  const pasteArea = document.getElementById('paste-area') as HTMLTextAreaElement | null;
  const importPasteBtn = document.getElementById('import-paste');
  const flashcard = document.getElementById('flashcard');
  const speakBtn = document.getElementById('speak-btn');

  if (dropZone) {
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('drag-over');
    });
    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('drag-over');
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('drag-over');
      const file = e.dataTransfer?.files[0];
      if (file) {
        handleFile(file);
      }
    });
    dropZone.addEventListener('click', () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.md,.txt';
      input.onchange = () => {
        if (input.files && input.files[0]) {
          handleFile(input.files[0]);
        }
      };
      input.click();
    });
  }

  if (importPasteBtn && pasteArea) {
    importPasteBtn.addEventListener('click', () => {
      const text = pasteArea.value;
      if (!text.trim()) {
        setMessage('粘贴区为空，请先输入词汇。', 'error');
        render();
        return;
      }
      importCardsFromText(text);
    });
  }

  if (flashcard) {
    flashcard.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#speak-btn')) return;
      flipCard();
    });
    flashcard.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        flipCard();
      }
    });
  }

  if (speakBtn) {
    speakBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const card = state.dueCards[state.currentIndex];
      if (card) {
        speak(card.hanzi);
      }
    });
  }

  document.querySelectorAll<HTMLButtonElement>('.rating-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const rating = Number(btn.dataset.rating);
      if (rating >= 1 && rating <= 4) {
        rateCurrentCard(rating);
      }
    });
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function handleKeydown(e: KeyboardEvent): void {
  if (state.dueCards.length === 0) return;

  if (e.key === ' ') {
    e.preventDefault();
    flipCard();
    return;
  }

  if (e.key >= '1' && e.key <= '4') {
    e.preventDefault();
    rateCurrentCard(Number(e.key));
    return;
  }

  if (e.key === 'ArrowRight') {
    e.preventDefault();
    showNextCard();
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    showPreviousCard();
  }
}

function init(): void {
  loadPersistedState();
  render();
  document.addEventListener('keydown', handleKeydown);
}

init();
